import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { WfRuntimeMenuApi } from '../services/wf-task-api.service';
import { RuntimeMenuNode } from '../services/wf-runtime-types';
import { WfUnmatchedHotfolderApi } from '../services/wf-unmatched-hotfolder-api.service';

/**
 * Phase 7 — runtime sidebar.
 *
 * <p>Renders the 3-level role-filtered menu tree returned by
 * {@code GET /api/wf/menu/runtime}: Category &gt; Workflow &gt; (Start | Step).
 *
 * <p>The tree is server-filtered to the current user's roles, so this
 * component renders whatever it receives without further filtering. Empty
 * branches are pruned server-side and don't reach the client.
 *
 * <h2>Routing convention</h2>
 *
 * <ul>
 *   <li>STEP node → {@code /wf-runtime/:wfKey/:taskKey} (the right pane
 *       shows the task list for that step).</li>
 *   <li>START node → {@code /wf-runtime/:wfKey/__start__} (Round 4 will
 *       open the start form; Round 2 just shows a placeholder).</li>
 * </ul>
 *
 * <p>The {@code __start__} sentinel is reserved — real BPMN
 * {@code taskDefinitionKey}s never start with double underscores.
 *
 * <h2>Expansion state</h2>
 *
 * <p>Tracked in-memory only. The first category is auto-expanded on load
 * for one-click access. Round 4 may persist expansion to localStorage so
 * users return to their last-open workflow.
 *
 * <h2>Styling</h2>
 *
 * <p>Scoped via {@code styles: [...]} per the wf-admin convention. The
 * outer markup follows the {@code sp-workflow-*} class structure used by
 * the rest of the SkyPlus shell so the runtime page slots in seamlessly.
 */
@Component({
  selector: 'wf-runtime-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div class="rt-sb">
      <div class="rt-sb-title" *ngIf="showHeader">
        <span class="material-icons">view_list</span>
        <span>Workflows</span>
      </div>

      <div class="rt-sb-loading" *ngIf="loading">
        <span class="material-icons spin">progress_activity</span>
        <span>Loading…</span>
      </div>

      <div class="rt-sb-empty" *ngIf="!loading && tree.length === 0">
        <span class="material-icons">inbox</span>
        <span>No workflows visible to your role</span>
      </div>

      <ng-container *ngIf="!loading && tree.length > 0">
        <ng-container *ngFor="let node of tree">

          <!-- Grouped: a category with workflow children -->
          <div class="rt-cat" *ngIf="node.type === 'CATEGORY'">
            <div class="rt-cat-head" (click)="toggle(catKey(node))">
              <span class="material-icons chev">
                {{ isExpanded(catKey(node)) ? 'expand_more' : 'chevron_right' }}
              </span>
              <span class="cat-nm">{{ node.label }}</span>
              <span class="cat-count">{{ node.children?.length || 0 }}</span>
            </div>
            <div class="rt-cat-body" *ngIf="isExpanded(catKey(node))">
              <ng-container *ngFor="let wf of node.children"
                            [ngTemplateOutlet]="wfTpl"
                            [ngTemplateOutletContext]="{ $implicit: wf }"></ng-container>
            </div>
          </div>

          <!-- Flat: a workflow promoted to the top level (menu_flat, no category group) -->
          <div class="rt-cat rt-flat" *ngIf="node.type !== 'CATEGORY'">
            <ng-container [ngTemplateOutlet]="wfTpl"
                          [ngTemplateOutletContext]="{ $implicit: node }"></ng-container>
          </div>

        </ng-container>
      </ng-container>

      <!-- One workflow node (head + steps). Reused under a category and at the top level. -->
      <ng-template #wfTpl let-wf>
            <div class="rt-wf">

              <!-- Global report shown directly under a category (e.g. "Reports") -->
              <a *ngIf="wf.type === 'REPORT'"
                 class="rt-step rt-step-report"
                 [routerLink]="nodeRoute(wf)"
                 routerLinkActive="selected">
                <span class="material-icons step-ico">{{ wf.icon || 'assessment' }}</span>
                <span class="step-nm">{{ wf.label }}</span>
              </a>

              <ng-container *ngIf="wf.type !== 'REPORT'">
              <div class="rt-wf-head" (click)="toggle(wfKey(wf))">
                <span class="material-icons chev">
                  {{ isExpanded(wfKey(wf)) ? 'expand_more' : 'chevron_right' }}
                </span>
                <span class="material-icons wf-ico"
                      [style.color]="wf.color || null"
                      *ngIf="wf.icon">{{ wf.icon }}</span>
                <span class="material-icons wf-ico" *ngIf="!wf.icon">account_tree</span>
                <span class="wf-nm">{{ wf.label }}</span>
              </div>

              <div class="rt-wf-body" *ngIf="isExpanded(wfKey(wf))">

              <!-- Phase F: dashboard landing for workflows that have one -->
                <a *ngIf="hasDashboard(wf)"
                   class="rt-step rt-step-dashboard"
                   [routerLink]="['/workflow', 'r', wf.wfKey, '__dashboard__']"
                   routerLinkActive="selected">
                  <span class="material-icons step-ico">dashboard</span>
                  <span class="step-nm">{{ wf.dashboardLabel || 'Dashboard' }}</span>
                </a>

                <a *ngFor="let step of stepChildren(wf)"
                   class="rt-step"
                   [class.read-only]="step.access === 'read'"
                   [routerLink]="nodeRoute(step)"
                   routerLinkActive="selected">

                  <span class="material-icons step-ico">
                    {{ stepIcon(step) }}
                  </span>

                  <span class="step-nm">{{ step.label }}</span>

                  <span class="badge ro" *ngIf="step.access === 'read'">read</span>

                  <span class="badge count"
                        *ngIf="step.type === 'STEP' && (step.pendingCount || 0) > 0">
                    {{ step.pendingCount }}
                  </span>
                </a>

                <!-- Phase B: officer queue for hot-folder files that didn't auto-match -->
                <a *ngIf="hasUnmatchedQueue(wf)"
                   class="rt-step rt-step-unmatched"
                   [routerLink]="['/workflow', 'r', wf.wfKey, '__unmatched__']"
                   routerLinkActive="selected">
                  <span class="material-icons step-ico">help_outline</span>
                  <span class="step-nm">Unmatched scans</span>
                  <span class="badge count" *ngIf="unmatchedCounts[wf.wfKey] > 0">
                    {{ unmatchedCounts[wf.wfKey] }}
                  </span>
                </a>

                <!-- Reports for this workflow, grouped under their own sub-header
                     (kept out of the step list so no report looks nested in another) -->
                <div class="rt-reports" *ngIf="reportChildren(wf).length">
                  <div class="rt-reports-hd">
                    <span class="material-icons">assessment</span> Reports
                  </div>
                  <a *ngFor="let rep of reportChildren(wf)"
                     class="rt-step rt-step-report"
                     [routerLink]="nodeRoute(rep)"
                     routerLinkActive="selected">
                    <span class="material-icons step-ico">{{ rep.icon || 'assessment' }}</span>
                    <span class="step-nm">{{ rep.label }}</span>
                  </a>
                </div>

              </div>
              </ng-container>
            </div>
      </ng-template>
    </div>
  `,
  styles: [`
    .rt-sb { padding: 8px 4px; font-size: 13px; }
 
    .rt-sb-title {
      display: flex; align-items: center; gap: 8px;
      padding: 12px 16px;
      font-weight: 600; font-size: 11px;
      letter-spacing: 0.06em; text-transform: uppercase;
      color: rgba(36, 64, 98, 0.65);
      border-bottom: 1px solid rgba(36, 64, 98, 0.08);
      margin-bottom: 6px;
    }
    .rt-sb-title .material-icons {
      font-size: 18px; color: rgba(36, 64, 98, 0.55);
    }
 
    .rt-sb-loading, .rt-sb-empty {
      display: flex; align-items: center; gap: 10px;
      padding: 28px 20px;
      color: rgba(0,0,0,0.45);
      font-size: 12px; font-style: italic;
    }
    .rt-sb-loading .material-icons,
    .rt-sb-empty   .material-icons {
      font-size: 20px;
    }
    .spin { animation: rt-spin 1s linear infinite; }
    @keyframes rt-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
 
    /* ─── Level 1 — Category ───────────────────────────────────────────── */
    .rt-cat { margin-bottom: 2px; }
 
    .rt-cat-head {
      display: flex; align-items: center; gap: 6px;
      padding: 9px 14px; margin: 0;
      cursor: pointer; user-select: none;
      transition: background 120ms ease, color 120ms ease;
    }
    .rt-cat-head:hover {
      background: rgba(36, 64, 98, 0.04);
      color: #244062;
    }
    .rt-cat-head .chev {
      font-size: 18px;
      color: rgba(36, 64, 98, 0.45);
      transition: transform 120ms ease;
    }
    .rt-cat-head .cat-nm {
      flex: 1;
      font-size: 11px; font-weight: 600;
      letter-spacing: 0.04em; text-transform: uppercase;
      color: rgba(36, 64, 98, 0.75);
    }
    .rt-cat-head .cat-count {
      font-size: 10px; font-weight: 600;
      padding: 2px 8px; border-radius: 999px;
      background: rgba(36, 64, 98, 0.08);
      color: rgba(36, 64, 98, 0.75);
      letter-spacing: 0;
    }
 
    /* ─── Level 2 — Workflow ───────────────────────────────────────────── */
    .rt-cat-body {
      padding: 2px 0 6px 12px;
      margin-left: 14px;
      border-left: 1px solid rgba(36, 64, 98, 0.08);
    }
 
    .rt-wf { margin-bottom: 1px; }
 
    .rt-wf-head {
      display: flex; align-items: center; gap: 8px;
      padding: 8px 12px;
      margin: 0 6px 0 4px;
      border-radius: 6px;
      cursor: pointer; user-select: none;
      transition: background 120ms ease;
    }
    .rt-wf-head:hover { background: rgba(36, 64, 98, 0.05); }
    .rt-wf-head .chev {
      font-size: 16px;
      color: rgba(36, 64, 98, 0.5);
      transition: transform 120ms ease;
    }
    .rt-wf-head .wf-ico {
      font-size: 16px;
      color: #244062;
      background: rgba(36, 64, 98, 0.08);
      padding: 4px;
      border-radius: 5px;
    }
    .rt-wf-head .wf-nm {
      flex: 1;
      font-size: 13px; font-weight: 500;
      color: #1f2937;
    }
 
    /* ─── Level 3 — Steps ──────────────────────────────────────────────── */
    .rt-wf-body {
      padding: 2px 0 6px 14px;
      margin-left: 22px;
      border-left: 1px solid rgba(36, 64, 98, 0.06);
    }
 
    .rt-step {
      position: relative;
      display: flex; align-items: center; gap: 9px;
      padding: 7px 10px;
      margin: 1px 6px 1px 0;
      border-radius: 5px;
      font-size: 12.5px;
      color: rgba(31, 41, 55, 0.85);
      text-decoration: none !important;
      cursor: pointer;
      transition: background 120ms ease, color 120ms ease, padding-left 120ms ease;
    }
    .rt-step:hover {
      background: rgba(36, 64, 98, 0.05);
      color: #244062;
      padding-left: 13px;
    }
    .rt-step .step-ico {
      /* Fixed, centred, clipped slot so every label lines up even when a
         report's icon is missing or an invalid glyph name. */
      width: 18px; min-width: 18px; max-width: 18px;
      text-align: center; overflow: hidden;
      font-size: 16px;
      color: rgba(36, 64, 98, 0.45);
      transition: color 120ms ease;
    }
    .rt-step:hover .step-ico { color: #244062; }
    .rt-step .step-nm {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
 
    .rt-step.read-only .step-ico { color: rgba(180, 113, 18, 0.7); }
    .rt-step.read-only .step-nm { color: rgba(31, 41, 55, 0.65); }
 
    .rt-step.selected {
      background: rgba(36, 64, 98, 0.10);
      box-shadow: inset 3px 0 0 #244062;
      padding-left: 13px;
    }
    .rt-step.selected .step-nm { color: #244062; font-weight: 500; }
    .rt-step.selected .step-ico { color: #244062; }
 
    /* ─── Badges ───────────────────────────────────────────────────────── */
    .badge {
      font-size: 10px; font-weight: 600;
      padding: 2px 8px; border-radius: 999px;
      line-height: 1.4;
      letter-spacing: 0.02em;
    }
    .badge.count {
      background: #244062;
      color: #ffffff;
      min-width: 18px;
      text-align: center;
    }
    .badge.ro {
      background: #fef3c7;
      color: #92400e;
      text-transform: uppercase;
      font-size: 9px;
      letter-spacing: 0.05em;
    }
      .rt-step-unmatched {
      border-top: 0.5px solid rgba(255, 255, 255, 0.08);
      margin-top: 4px;
      padding-top: 10px;
    }
    .rt-step-unmatched .step-ico { color: rgba(255, 255, 255, 0.6); }
    .rt-step-dashboard {
      background: rgba(36, 64, 98, 0.04);
      font-weight: 600;
    }
    .rt-step-dashboard:hover {
      background: rgba(36, 64, 98, 0.08);
    }
    .rt-step-dashboard .step-ico {
      color: #244062;
    }
    /* Reports sub-group inside a workflow body */
    .rt-reports {
      margin-top: 4px;
      padding-top: 4px;
      border-top: 1px dashed rgba(36, 64, 98, 0.14);
    }
    .rt-reports-hd {
      display: flex; align-items: center; gap: 5px;
      padding: 4px 10px 3px;
      font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
      color: #8a94a3;
    }
    .rt-reports-hd .material-icons { font-size: 13px; }
    .rt-step-report .step-ico { color: #244062; }
  `]
  
})
export class WfRuntimeSidebarComponent implements OnInit,OnDestroy {

    /**
   * When false, the "Workflows" title row above the tree is hidden. Used
   * when this sidebar is embedded inside {@code WorkflowSidebarComponent}
   * which provides its own unified header for both legacy and kernel
   * sections. Defaults to true to preserve standalone behaviour.
   */
  @Input() showHeader = true;

   tree: RuntimeMenuNode[] = [];
  loading = true;
  expanded = new Set<string>();

  /** Per-workflow count of files parked in .unmatched/ (Phase B). Keyed by
   *  wfKey; populated for workflows whose menu node has parkUnmatched=true. */
  unmatchedCounts: Record<string, number> = {};

  /** Emits on destroy to unsubscribe from long-lived streams. */
  private readonly destroy$ = new Subject<void>();

  /** True once the first fetch has populated the tree — prevents the
   * auto-expand from re-running and collapsing user choices on refresh. */


  constructor(
    private menuApi: WfRuntimeMenuApi,
    private router: Router,
    private unmatchedApi: WfUnmatchedHotfolderApi
  ) {}

  ngOnInit(): void {
    // Initial load
    this.loadMenu();

    // Refresh on every navigation end — task completion / claim /
    // forward all navigate, so this catches every case where a badge
    // should change without an explicit subscribe-and-emit pattern.
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => this.loadMenu());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Fetch the role-filtered menu tree and update badges in place. */
  /** Fetch the role-filtered menu tree and update badges in place. */
  private loadMenu(): void {
    this.menuApi.tree().subscribe({
      next: tree => {
        this.tree = tree || [];
        this.loading = false;
        // No auto-expand — every category and workflow starts collapsed
        // and stays independent. Users click to expand what they need.
        this.refreshUnmatchedCounts();
      },
      error: err => {
        console.error('[wf-runtime] menu/runtime fetch failed', err);
        this.loading = false;
      }
    });
  }

  // ─── Expansion state ─────────────────────────────────────────────────────

  toggle(key: string): void {
    if (this.expanded.has(key)) {
      this.expanded.delete(key);
    } else {
      this.expanded.add(key);
    }
  }

  isExpanded(key: string): boolean {
    return this.expanded.has(key);
  }

  catKey(cat: RuntimeMenuNode): string { return `CAT:${cat.label}`; }
  wfKey(wf:  RuntimeMenuNode): string { return `WF:${wf.wfKey}`; }

  // ─── Routing helpers ─────────────────────────────────────────────────────

  /**
   * Resolve the routerLink for a step / start node. Returns a string array
   * suitable for {@code [routerLink]} binding. Round 2 routes both START
   * and STEP nodes to the task-list page; Round 4 will switch START to its
   * own start-form route.
   */
  stepRoute(node: RuntimeMenuNode): string[] {
    const wf = node.wfKey ?? '';
    const tk = node.type === 'START' ? '__start__' : (node.taskKey ?? '');
    return ['/workflow', 'r', wf, tk];
  }

  /** Route for any leaf node — REPORT nodes go to the report viewer. */
  nodeRoute(node: RuntimeMenuNode): string[] {
    if (node.type === 'REPORT') {
      return ['/workflow', 'r', '__report__', node.reportKey ?? ''];
    }
    return this.stepRoute(node);
  }

  /** Leading icon for a leaf node in the workflow body. */
  stepIcon(node: RuntimeMenuNode): string {
    if (node.type === 'START') return 'play_arrow';
    if (node.type === 'REPORT') return node.icon || 'assessment';
    return 'check_circle_outline';
  }

  /** Workflow-body children that are steps (not reports). */
  stepChildren(wf: RuntimeMenuNode): RuntimeMenuNode[] {
    return (wf.children ?? []).filter(c => c.type !== 'REPORT');
  }

  /** Workflow-scoped reports, shown under their own "Reports" sub-header. */
  reportChildren(wf: RuntimeMenuNode): RuntimeMenuNode[] {
    return (wf.children ?? []).filter(c => c.type === 'REPORT');
  }

  /** True if this workflow has a hot-folder park queue we should surface.
   *  Driven by the per-workflow {@code parkUnmatched} flag from the runtime
   *  menu (set when its hot folder's unmatchedBehavior === 'park'). */
  hasUnmatchedQueue(wf: RuntimeMenuNode): boolean {
    return wf.parkUnmatched === true;
  }

 /** True if this workflow has a configured dashboard we should surface
   *  as a "landing" entry at the top of the workflow's step list.
   *
   *  Hardcoded for now — same pattern as {@link hasUnmatchedQueue}.
   *  When the menu/runtime backend endpoint adds a {@code hasDashboard}
   *  flag per workflow node, this becomes {@code wf.hasDashboard === true}.
   */
  hasDashboard(wf: RuntimeMenuNode): boolean {
    return wf.hasDashboard === true;
  }

  /** Re-fetch the count of parked files per park-enabled workflow (driven by
   *  the menu's parkUnmatched flag, not a hardcoded list). */
  private refreshUnmatchedCounts(): void {
    const parkKeys = new Set<string>();
    for (const cat of this.tree) {
      for (const wf of cat.children || []) {
        if (wf.parkUnmatched === true && wf.wfKey) { parkKeys.add(wf.wfKey); }
      }
    }
    for (const wfKey of parkKeys) {
      this.unmatchedApi.list(wfKey).subscribe({
        next: files => { this.unmatchedCounts[wfKey] = files?.length ?? 0; },
        error: () => { /* swallow — don't break the sidebar */ }
      });
    }
  }

}